/**
 * Safe scalars for a wallet `error` callback that arrived as a plain object.
 *
 * ChargeBee rejects Apple/Google Pay with the whole payment intent. We never
 * log that object — `payer_info` and `checkout_details` are the customer's
 * email, phone, address and IP — so `String(err)` becomes "[object Object]"
 * and the reason walker reports "Unknown error" when there is no
 * `error_detail`. These fields are the remainder that is safe to keep: key
 * names, allow-listed statuses, and an id shaped like a vendor token.
 */

import { asOpaqueToken, asRecord, readProperty } from "./safeErrorRead";

/** ChargeBee's status for an intent no payment was ever attempted on. */
const UNATTEMPTED_INTENT_STATUS = "inited";

/**
 * ChargeBee payment-intent `status` values. A sheet that hung and then failed
 * often comes back `in_progress` with no attempt.
 */
const ALLOWED_INTENT_STATUSES = new Map(
    [
        UNATTEMPTED_INTENT_STATUS,
        "in_progress",
        "authorized",
        "consumed",
        "expired",
    ].map((status) => [status, status])
);

/**
 * ChargeBee payment-attempt `status` values. `refused` is the issuer decline
 * we already classify; anything else on an unknown error is the missing
 * explanation.
 */
const ALLOWED_ATTEMPT_STATUSES = new Map(
    [
        "inited",
        "requires_identification",
        "requires_challenge",
        "requires_redirection",
        "authorized",
        "refused",
        "voided",
        "pending_authorization",
        "expired",
    ].map((status) => [status, status])
);

const ALLOWED_ERROR_OBJECTS = new Map(
    ["payment_intent", "payment_attempt", "gateway_error_detail"].map(
        (object) => [object, object]
    )
);

/**
 * Google Pay `PaymentsError.statusCode` — the full enum the Google Pay API
 * for Web defines, not an HTTP status (the HTTP reader in
 * `resolveFailureReason` ignores it, which is why a cancelled or failed sheet
 * used to land as "Unknown error" with no other facet).
 * https://developers.google.com/pay/api/web/reference/response#PaymentsError
 */
const ALLOWED_WALLET_STATUS_CODES = new Map(
    [
        ["canceled", "CANCELED"],
        ["developer_error", "DEVELOPER_ERROR"],
        ["merchant_account_error", "MERCHANT_ACCOUNT_ERROR"],
        ["internal_error", "INTERNAL_ERROR"],
        ["buyer_account_error", "BUYER_ACCOUNT_ERROR"],
    ]
);

const KEY_SHAPE = /^[A-Za-z_][A-Za-z0-9_]*$/;
const MAX_SHAPE_KEYS = 16;
/**
 * `MAX_SHAPE_KEYS` bounds how many keys are shown, not how long any one of
 * them is — a single property name has no size limit in JS. The longest real
 * field seen on a ChargeBee intent/attempt is `active_payment_attempt` (23
 * chars), so this only ever excludes a degenerate or hostile key, never a
 * real one.
    */
const MAX_KEY_LENGTH = 64;
const MAX_INTENT_ID_LENGTH = 80;

export type WalletErrorDiagnostics = {
    /** Sorted enumerable keys of the thrown object. Names only, never values. */
    errorShape?: string;
    /** ChargeBee `object` discriminator, when it is one we know. */
    errorObject?: string;
    intentId?: string;
    intentStatus?: string;
    attemptStatus?: string;
    /** Google Pay `statusCode` (`CANCELED` / `DEVELOPER_ERROR` / `INTERNAL_ERROR` / …). */
    walletStatusCode?: string;
};

function allowlisted(
    value: unknown,
    allowed: ReadonlyMap<string, string>
): string | undefined {
    if (typeof value !== "string") return undefined;

    const token = value.trim();

    return token ? allowed.get(token.toLowerCase()) : undefined;
}

function asIntentId(value: unknown): string | undefined {
    return asOpaqueToken(value, MAX_INTENT_ID_LENGTH);
}

/**
 * Sorted, filtered key names, capped at `MAX_SHAPE_KEYS` so one grossly wide
 * object can't blow up the field. The cap truncates alphabetically — visibly,
 * via the `+N` suffix, rather than silently dropping keys a reader would
 * otherwise assume were absent from the source object.
 */
function readErrorShape(source: Record<string, unknown>): string | undefined {
    try {
        const keys = Object.keys(source)
            .filter((key) => key.length <= MAX_KEY_LENGTH && KEY_SHAPE.test(key))
            .sort((left, right) => left.localeCompare(right, "en"));

        if (keys.length === 0) return undefined;

        const shown = keys.slice(0, MAX_SHAPE_KEYS).join(",");
        const omitted = keys.length - MAX_SHAPE_KEYS;

        return omitted > 0 ? `${shown},+${omitted}` : shown;
    } catch {
        return undefined;
    }
}

function looksLikeIntent(source: Record<string, unknown>): boolean {
    if (allowlisted(readProperty(source, "object"), ALLOWED_ERROR_OBJECTS) ===
        "payment_intent") {
        return true;
    }

    return (
        asRecord(readProperty(source, "active_payment_attempt")) !== null ||
        Array.isArray(readProperty(source, "payment_attempts"))
    );
}

function readAttempt(
    source: Record<string, unknown>
): Record<string, unknown> | null {
    const active = asRecord(readProperty(source, "active_payment_attempt"));
    if (active) return active;

    const attempts = readProperty(source, "payment_attempts");
    if (!Array.isArray(attempts)) return null;

    // Newest last in the ChargeBee list; the one that just failed is what
    // we need a status for.
    for (let index = attempts.length - 1; index >= 0; index -= 1) {
        const attempt = asRecord(attempts[index]);
        if (attempt) return attempt;
    }

    return null;
}

function isPaymentAttempt(source: Record<string, unknown>): boolean {
    return (
        allowlisted(readProperty(source, "object"), ALLOWED_ERROR_OBJECTS) ===
        "payment_attempt"
    );
}

/** An attempt held by `source`, or `source` itself when it is one. */
function readAttemptFrom(
    source: Record<string, unknown>
): Record<string, unknown> | null {
    return readAttempt(source) ?? (isPaymentAttempt(source) ? source : null);
}

/** Google's own `PaymentsError.statusCode`, and nothing else. */
function readGooglePayStatusCode(
    source: Record<string, unknown>
): string | undefined {
    return allowlisted(readProperty(source, "statusCode"), ALLOWED_WALLET_STATUS_CODES);
}

function readWalletStatusCode(
    source: Record<string, unknown>
): string | undefined {
    return (
        readGooglePayStatusCode(source) ??
        allowlisted(readProperty(source, "status_code"), ALLOWED_WALLET_STATUS_CODES)
    );
}

/** The SDK wrappers a wallet rejection arrives in: `{ error }` or `{ cause }`. */
function readWrapped(source: Record<string, unknown>): Record<string, unknown>[] {
    return [
        asRecord(readProperty(source, "error")),
        asRecord(readProperty(source, "cause")),
    ].filter((value): value is Record<string, unknown> => value !== null);
}

function readDiagnosticsFrom(
    source: Record<string, unknown>
): WalletErrorDiagnostics {
    const nested = readWrapped(source);

    const intent =
        (looksLikeIntent(source) ? source : undefined) ??
        nested.find(looksLikeIntent);

    const attemptSource =
        (intent && readAttempt(intent)) ??
        [source, ...nested]
            .map(readAttemptFrom)
            .find((attempt): attempt is Record<string, unknown> => attempt !== null);

    return {
        errorShape: readErrorShape(source),
        // The `object` discriminator is reported for any of the three known
        // shapes — an attempt or gateway-detail source has one just as much
        // as an intent does — but `intentId`/`intentStatus` below are read
        // only from a confirmed intent: falling back to `source` there would
        // report a payment *attempt's* own `id`/`status` (or any bystander
        // object's `id`) as if it were the intent's.
        errorObject: allowlisted(
            readProperty(intent ?? source, "object"),
            ALLOWED_ERROR_OBJECTS
        ),
        intentId: intent ? asIntentId(readProperty(intent, "id")) : undefined,
        intentStatus: intent
            ? allowlisted(readProperty(intent, "status"), ALLOWED_INTENT_STATUSES)
            : undefined,
        attemptStatus: attemptSource
            ? allowlisted(
                readProperty(attemptSource, "status"),
                ALLOWED_ATTEMPT_STATUSES
            )
            : undefined,
        // Searched through the same wrappers as the intent: ChargeBee hands
        // Google's `PaymentsError` back wrapped as `{ error }` as readily as
        // bare, and a top-level-only read dropped the one code that says
        // why the sheet failed.
        walletStatusCode: [source, ...nested]
            .map(readWalletStatusCode)
            .find((code): code is string => code !== undefined),
    };
}

/**
 * Whether a wallet rejection carries Google Pay's `CANCELED`.
 *
 * Only Google's own `PaymentsError.statusCode` counts, bare or in one SDK
 * wrapper — not a `status_code` from some other object. `CANCELED` is also
 * what Google returns for an unsupported browser, an insecure context, a call
 * while a sheet is already up, or a rejected request, so this alone does not
 * prove a dismissal: the caller must also know the customer tapped and the
 * sheet had not yet handed the payment back. Never throws.
 */
export function isGooglePayCanceled(error: unknown): boolean {
    try {
        const source = asRecord(error);
        if (!source) return false;

        const statusCode = [source, ...readWrapped(source)]
            .map(readGooglePayStatusCode)
            .find((code): code is string => code !== undefined);

        return statusCode === "CANCELED";
    } catch {
        return false;
    }
}

/**
 * Whether the rejection is an intent the gateway never received: `inited`
 * and no payment attempt on it. `intentStatus` is only read off a confirmed
 * intent, so it cannot be some other object's status.
 */
export function isUnattemptedIntent({
    intentStatus,
    attemptStatus,
}: WalletErrorDiagnostics): boolean {
    return (
        intentStatus === UNATTEMPTED_INTENT_STATUS && attemptStatus === undefined
    );
}

/**
 * Shape of a wallet rejection, safe to put on a log. Returns `{}` when there
 * is nothing to say or when reading the value throws — same contract as
 * `getFailureDetails`, because this also runs on the payment error path.
 */
export function getWalletErrorDiagnostics(
    error: unknown
): WalletErrorDiagnostics {
    try {
        const source = asRecord(error);
        return source ? readDiagnosticsFrom(source) : {};
    } catch {
        return {};
    }
}
